import { api } from "./client";
import type {
  DocumentShare,
  DownloadUrl,
  DrawingSetContainer,
  ShareCreateInput,
  ShareRow,
  UploadedDrawing,
} from "@/types";

/** GET {Jobs}/portal/shares?job_id — S5: staff share list, all statuses, with
 *  file facts and the latest decision. */
export function listShares(jobId: number, signal?: AbortSignal): Promise<DocumentShare[]> {
  return api.get<DocumentShare[]>("jobs", "portal/shares", {
    query: { job_id: jobId },
    signal,
  });
}

/** POST {Jobs}/portal/share — S1: create a draft share for one drawing. */
export function createShare(input: ShareCreateInput): Promise<ShareRow> {
  return api.post<ShareRow>("jobs", "portal/share", { body: input });
}

/** POST {Jobs}/portal/share/{id}/send — S2: draft → sent; flips a superseded
 *  predecessor and pushes to the job's portal users. */
export function sendShare(shareId: number): Promise<ShareRow> {
  return api.post<ShareRow>("jobs", `portal/share/${shareId}/send`, {});
}

/** POST {Jobs}/portal/share/{id}/withdraw — S3: sent/viewed → withdrawn. */
export function withdrawShare(shareId: number): Promise<ShareRow> {
  return api.post<ShareRow>("jobs", `portal/share/${shareId}/withdraw`, {});
}

/** DELETE {Jobs}/portal/share/{id} — S4: drafts only. */
export function deleteShare(shareId: number): Promise<unknown> {
  return api.delete<unknown>("jobs", `portal/share/${shareId}`);
}

/** POST {Jobs}/portal/drawing/upload — multipart staff upload: pre-inserts the
 *  drawing row, PUTs bytes to R2, patches metadata. */
export function uploadDrawing(opts: {
  file: File;
  jobId: number;
  drawingSetId: number;
  title?: string;
}): Promise<UploadedDrawing> {
  const fd = new FormData();
  fd.append("file", opts.file, opts.file.name);
  fd.append("job_id", String(opts.jobId));
  fd.append("drawing_set_id", String(opts.drawingSetId));
  fd.append("file_name", opts.file.name);
  if (opts.file.type) fd.append("mime_type", opts.file.type);
  if (opts.title) fd.append("title", opts.title);
  return api.post<UploadedDrawing>("jobs", "portal/drawing/upload", { body: fd });
}

/** DELETE {Jobs}/portal/drawing/{id} — soft delete + R2 object delete. */
export function deleteDrawing(drawingId: number): Promise<unknown> {
  return api.delete<unknown>("jobs", `portal/drawing/${drawingId}`);
}

/** GET {Jobs}/staff/drawing/{id}/download-url — staff presign for any live
 *  file (drafts included; no share-visibility gate). Use the URL immediately. */
export function getDownloadUrl(drawingId: number): Promise<DownloadUrl> {
  return api.get<DownloadUrl>("jobs", `staff/drawing/${drawingId}/download-url`);
}

/** POST {Jobs}/staff/jobs/{job_id}/drawing_sets — minimal non-current container
 *  set for the upload flow (deliberately NOT the create_drawing_set workflow). */
export function createContainerSet(jobId: number, label?: string): Promise<DrawingSetContainer> {
  return api.post<DrawingSetContainer>("jobs", `staff/jobs/${jobId}/drawing_sets`, {
    body: label ? { label } : {},
  });
}